<script setup>
import { CdrModal, CdrHeadingSerif, CdrText, CdrButton, IconCheckFill } from '@rei/cedar';
import { computed } from 'vue';
import { currencyFormatter } from '../../../../common/utils';

const props = defineProps({
  opened: {
    type: Boolean,
    default: false,
  },
  cardNumber: {
    type: String,
    default: '',
  },
  cardBalance: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['close', 'save']);

const hasCardBalance = computed(() => props.cardBalance > 0);
const formatedCardBalance = computed(() => currencyFormatter.format(props.cardBalance));
const alertClasses = computed(() => [
  'card-balance-modal__alert',
  { 'card-balance-modal__alert--success': hasCardBalance.value },
]);

// Splits card number into chunks of 4 characters
const chunkedCardNumber = computed(() =>
  props.cardNumber
    .replace(/(.{4})/g, '$1-')
    .trim()
    .split('-'),
);

// Copies card number without spaces to clipboard
const handleCopy = (event) => {
  const selectedText = window.getSelection().toString();
  const trimmedText = selectedText.replace(/\s/g, '');

  event.clipboardData.setData('text/plain', trimmedText);
  event.preventDefault();
};
</script>

<template>
  <cdr-modal
    :opened="props.opened"
    label="Card balance"
    overlay-class="card-balance-modal__overlay"
    data-ui="card-balance-modal__component"
    @closed="emit('close', 'cancel')"
  >
    <template #title>
      <cdr-heading-serif
        class="card-balance-modal__title"
        data-ui="card-balance-modal__title"
        tag="h3"
      >
        Card balance for:
        <span class="card-balance-modal__title-number" @copy="handleCopy">
          <span v-for="(chunk, index) in chunkedCardNumber" :key="index">{{ chunk }}&nbsp; </span>
        </span>
      </cdr-heading-serif>
    </template>
    <div class="card-balance-modal">
      <div :class="alertClasses" data-ui="card-balance-modal__balance-alert" role="alert">
        <icon-check-fill />
        <cdr-text data-ui="card-balance-modal__balance-number">
          {{ formatedCardBalance }}
        </cdr-text>
      </div>

      <div
        v-if="hasCardBalance"
        class="card-balance-modal__info"
        data-ui="card-balance-modal__save-card-info"
      >
        <cdr-text strong>Save this card?</cdr-text>
        <cdr-text>Add it to your account for easy use later, online or in store.</cdr-text>
      </div>
      <div v-else class="card-balance-modal__info" data-ui="card-balance-modal__no-balance-info">
        <cdr-text strong>This card has been spent.</cdr-text>
      </div>

      <div
        v-if="hasCardBalance"
        class="card-balance-modal__footer"
        data-ui="card-balance-modal__save-card-actions"
      >
        <cdr-button data-ui="card-balance-modal__save-card-button-action" @click="emit('save')">
          Save to online account
        </cdr-button>
        <cdr-button
          data-ui="card-balance-modal__save-close-button-action"
          modifier="secondary"
          @click="emit('close', 'notNow')"
        >
          Not now
        </cdr-button>
      </div>
      <div
        v-else
        class="card-balance-modal__footer"
        data-ui="card-balance-modal__no-balance-actions"
      >
        <cdr-button
          data-ui="card-balance-modal__close-modal-not-balance-button-action"
          @click="emit('close', 'cancel')"
        >
          Close
        </cdr-button>
      </div>
    </div>
  </cdr-modal>
</template>

<style lang="scss" scoped>
$icon-size: 32px;

.card-balance-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $cdr-space-one-x;
  padding: $cdr-space-two-x $cdr-space-one-and-a-half-x;
  border-radius: $cdr-radius-softer;
  background-color: $cdr-color-background-secondary;
  border: 1px solid $cdr-color-border-primary;

  &__overlay {
    --cdr-modal-overlay-background-color: #{rgba($cdr-color-background-secondary, 0.9)};

    &.cdr-modal__overlay_15-0-1 {
      backdrop-filter: none;
    }
  }

  &__title {
    &.cdr-heading-serif_15-0-1 {
      @include cdr-text-heading-serif-600;
    }

    &-number {
      display: inline-block;
      font-weight: $cdr-text-default-weight;
    }
  }

  &__alert {
    display: flex;
    gap: $cdr-space-three-quarter-x;
    border-radius: $cdr-radius-softer;
    background-color: $cdr-color-background-primary;
    border: 1px solid $cdr-color-border-primary;
    padding: $cdr-space-inset-three-x-squish;

    &--success {
      background-color: $cdr-color-background-message-success;
      border: 1px solid $cdr-color-border-success;

      svg {
        fill: $cdr-color-icon-message-success;
      }
    }

    svg {
      width: $icon-size;
      height: $icon-size;
    }

    p {
      @include cdr-text-utility-sans-strong-600;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $cdr-space-half-x;
    padding-top: $cdr-space-quarter-x;
    text-align: center;

    p:first-child {
      @include cdr-text-utility-sans-strong-400;
    }

    p:nth-child(2) {
      @include cdr-text-utility-sans-300;
    }
  }

  &__footer {
    display: flex;
    gap: $cdr-space-three-quarter-x;
    padding-top: $cdr-space-quarter-x;

    @media (max-width: $cdr-breakpoint-sm) {
      flex-direction: column;
      align-items: center;
    }
  }
}
</style>
